/* @flow */
/* eslint-env es6,browser */

import * as u from './utils';
import adconfig from './adconfig';
import adrotation from './adrotation';
import { BrowserUtil } from './utils/browserutil';

/**
 * @description Implements functionality for ad loading
 * @exports adloader
 * @requires {@link module:utils}
 * @requires {@link module:adconfig}
 * @requires {@link module:adrotation}
 */
class _adloader {

  iframeHolder: string;
  sWrapper: string;
  tidDelay: number;
  delayList: Array<iframeInfo>;
  delayTimeout: number;
  doc: Document;
  win: window;
  adInfo: adPosInfoWrapper;
  adPage: string;
  /**
   * @private
   * @ignore
   */
  constructor() {
    this.iframeHolder = '';
    this.sWrapper = '';
    this.tidDelay = 0;
    this.delayList = [];
    this.delayTimeout = 0;
    this.adInfo = {};
    this.adPage = '';
    this.doc = document;
    this.win = window;
  }

  /**
   * Initialize the object
   * @param {window} w - window object
   * @param {window.document} d - document object
   * @param {globalParams} g - global param object
   */
  init(w: window, d: window.document, g: globalParam) {
    this.iframeHolder = g.iframeHolder;
    this.sWrapper = g.sWrapper;
    this.adPage = g.adPage;
    this.doc = d;
    this.win = w;

    //preload ad page
    u.preloadResource(this.doc, this.adPage);
  }

  /**
   * setter for delay timeout - delay between loading 2 ad positions
   * @param {number} t - timeout in millsec
   */
  setDelayTimeout(t: number) {
    this.delayTimeout = t;
  }

  _cleanAdFrame(doc: Document, adDivObj: HTMLElement, adFrameID: string) {
    let oldIF: HTMLIFrameElement = ((doc.getElementById(adFrameID): any): HTMLIFrameElement);
    //clean up old iframe
    if (oldIF) {
      oldIF.src = '';
      if (oldIF.documentWindow) {
        //$FlowFixMe
        oldIF.documentWindow.document = null;
        //$FlowFixMe
        oldIF.documentWindow = null;
      }
    }

    //empty content of the ad wrapper div now
    try {
      /* eslint-disable no-unmodified-loop-condition*/
      while (adDivObj && adDivObj.firstChild) {
        /* eslint-enable no-unmodified-loop-condition*/
        adDivObj.removeChild(adDivObj.firstChild);
      }
    } catch (e) {/*Ignore exception here */ }
  }

  _createAdIframe(adUrl: ?string, adDivObj: HTMLElement, adFrameID: string, as: posConfigInfo){
    let doc = adDivObj.ownerDocument;
    this._cleanAdFrame(doc, adDivObj, adFrameID);
    let newIF = u.createIframe(doc, adFrameID, as.width, as.height, adUrl);
    if (typeof as.MarginHeight !== 'undefined') {
      newIF.marginHeight = String(as.MarginHeight);
    }

    // Dynamically calculating the iframe height if dynHeight is true
    if(typeof as.dynHeight !== 'undefined' && as.dynHeight === true) {
      u.addEvent(newIF, 'load', () => u.setIfrHeight(adFrameID));
    }

    //insert new iframe
    adDivObj.appendChild(newIF);
    return newIF;
  }

  // getter for
  _getAdDiv(adDiv: string): null | HTMLElement {
    let adDivObj = null;
    adDivObj = document.getElementById(adDiv);
    if (adDivObj) {
      return adDivObj;
    }
    if (!this.iframeHolder.length) {
      return adDivObj;
    }
    var iframeHolder = ((document.getElementById(this.iframeHolder): any): HTMLIFrameElement);
    if (!iframeHolder) {
      return adDivObj;
    }
    let d = u.getIfrDoc(iframeHolder);
    if(d) {
      adDivObj = d.getElementById(adDiv);
    }
    return adDivObj;
  }

  //Utility functions for ad rotation
  //create container div for given ad position to hold ad rotation iframe
  _addIframeDivs(fID: string): boolean {
    var fIDadDiv = fID + this.sWrapper;
    var adDivObj = this._getAdDiv(fIDadDiv);
    if (adDivObj) {
      if (this._getAdDiv(fID + 'One') && this._getAdDiv(fID + 'Two')) {
        return true;    //already created
      }
      var tmpframe = `<div id='${fID}One' style='display: block;top: 0px;left: 0px;background-color: transparent;'></div>`;
      tmpframe += `<div id='${fID}Two' style='display: none;top: 0px;left: 0px;background-color: transparent;'></div>`;
      adDivObj.innerHTML = tmpframe;
      return true;
    }
    return false;
  }

  //this function is used to find active/inactive adframe during ad rotation
  //return object containing info about active & inactive frame & container
  _getActiveInactiveFrame(adPos: string): activeInactiveInfo {

    var ret = {}, active, adDivObj = this._getAdDiv(adPos + 'One');
    if (adDivObj && adDivObj.style.display !== 'none') {
      ret.activeContainer = adPos + 'One';
      ret.activeFrame = adPos + 'FrameOne';
      ret.inactiveContainer = adPos + 'Two';
      ret.inactiveFrame = adPos + 'FrameTwo';
    } else if (this._getAdDiv(adPos + 'Two')) {
      ret.activeContainer = adPos + 'Two';
      ret.activeFrame = adPos + 'FrameTwo';
      ret.inactiveContainer = adPos + 'One';
      ret.inactiveFrame = adPos + 'FrameOne';
    }
    //ensure active frame is visible
    active = this._getAdDiv(ret.activeContainer);
    if (active) {
      active.style.display = 'block';
    }
    return ret;
  }

  //this wrapper function will take care of returning correct ad frame details
  //irrespective of ad rotation enabled position or not
  _getAdFrame(adPos: string, bActive: boolean = false): adframeConfig {

    //return data will be adPos, adFrameId, adContainerId, adRotation
    var adFrameId = adPos, adContainer = adPos + this.sWrapper, adRotationFrame = false;

    //adRotation is enabled for specific positions only
    //configured in x89 position
    if ((adrotation.getPositions().indexOf(adPos) !== -1) &&
      (this._getAdDiv(adPos + 'One') && this._getAdDiv(adPos + 'Two'))) {
      var adFrame = this._getActiveInactiveFrame(adPos);

      //get the required ID for div & frame as per bActive flag
      adFrameId = bActive ? adFrame.activeFrame : adFrame.inactiveFrame;
      adContainer = bActive ? adFrame.activeContainer : adFrame.inactiveContainer;
      adRotationFrame = true;
    }

    return {
      id: adPos,
      fID: adFrameId,
      cID: adContainer,
      adRotation: adRotationFrame
    };
  }

  _isOldIE() {
    return (BrowserUtil.getShortName() === 'ie' && BrowserUtil.getMajorVersion() <= 10);
  }

  // load ad in iframe depending on position.
  _loadAdContent(adUrl: string, adFrameInfo: adframeConfig, as: posConfigInfo) {
    let adDivObj = this._getAdDiv(adFrameInfo.cID);
    if (!adDivObj) {
      return;
    }

    // load ads without blankPhnx.html only for old IE browsers
    if (as.loadingMethod === 2 || this._isOldIE()) {
      this._loadAdContentInOldIE(adDivObj, adFrameInfo, as);
      return;
    }

    // For all other browsers load ads using blankPhnx.html
    this._createAdIframe(adUrl, adDivObj, adFrameInfo.fID, as);
  }

  _loadAdContentInOldIE(adDivObj: HTMLElement, adFrameInfo: adframeConfig, as: posConfigInfo) {
    let newIF = this._createAdIframe('javascript:false', adDivObj, adFrameInfo.fID, as);
    if (!newIF) {
      return;
    }
    let info = this.adInfo[adFrameInfo.id];
    if (!info) {
      return;
    }
    if (adFrameInfo.adRotation) {
      let adFrame = this._getActiveInactiveFrame(adFrameInfo.id);
      if (u.isString(adFrame.activeContainer) && u.isString(adFrame.inactiveContainer)) {
        u.addEvent(newIF, 'load', () => this._toggleAd(adFrame.activeContainer, adFrame.inactiveContainer));
      }
    }

    //let us write ad content
    let ifrDoc = u.getIfrDoc(newIF);
    try {
      if (ifrDoc) {
        ifrDoc.open();
        ifrDoc.write(`<script>window.top.__as.populateCustomVar(window, document, '${adFrameInfo.id}');</script>${info.content}`);
        //not doing ifrDoc close for old IE browsers
        if ((as.loadingMethod === 2) && !this._isOldIE()) {
          ifrDoc.close();
        }
      }
    } catch (e) {
      u.logDebug(`loadAdContent ${adFrameInfo.id} iframe doc write failed: ${e.message}`);
    }
  }

  _loadAd(obj: iframeInfo) {
    //extend custom vars and populate current position as custom var
    u.extend(true, obj.customVars, { UOL_POS: obj.pos });
    var adPos = obj.pos,
      adFrameInfo = this._getAdFrame(adPos, false),
      //we got the ad position & the data needs to be written inside it.
      adPosInfo = {
        id: adFrameInfo.id,
        content: obj.data,
        customVars: obj.customVars,
        adRotation: adFrameInfo.adRotation
      }, as = adconfig.getPosSettings(adFrameInfo.id);
    this.adInfo[adPos] = adPosInfo;
    this._loadAdContent(this.adPage, adFrameInfo, as);
  }

  _loadDelayedAds() {
    //reset the timerID
    this.tidDelay = 0;
    if (this.delayList.length) {
      let ind = this.delayList.findIndex(o => adconfig.getPosSettings(o.pos).aFold),
        obj = this.delayList.splice(ind < 0 ? 0 : ind, 1)[0],
        as = adconfig.getPosSettings(obj.pos);

      //if the ad has ad rotation position, lets create iframes here.
      if (adrotation.getPositions().indexOf(obj.pos) !== -1 && as.iframe !== false && !this._isOldIE()) {
        this._addIframeDivs(obj.pos);
      }
      this._loadAd(obj);
    }
    this._setDelayTimer();
  }

  _setDelayTimer(/*bStaticAd*/) {
    if (!this.tidDelay && this.delayList.length) {
      let that = this;
      this.tidDelay = setTimeout(() => that._loadDelayedAds(), this.delayTimeout);
    }

    //there are pending ads to be loaded
    if (this.tidDelay) {
      return;
    }

    //start ad rotation timer here
    adrotation.trigger();
  }

  _loadIframeAd(obj: iframeInfo) {
    //add to the delayList & start the timer
    this.delayList.push(obj);
    this._setDelayTimer();
  }

  _loadNonIframeAd(adPos: string, data: string) {
    var adFrame = this._getAdFrame(adPos).cID;
    var adDivObj = this._getAdDiv(adFrame);
    if (adDivObj) {
      adDivObj.innerHTML = data;
    }
  }

  //loading global script/css ad requests
  _loadGlobalScriptCssAd(data: string) {
    var info: {js: Array<string>, css: Array<string>} = u.extend(true, {}, { js: [], css: [] }, u.parseJSON(this.win, data)),
      numScripts: number = info.js.length,
      numStyles: number = info.css.length, i = 0;
    for (i = 0; i < numScripts; i++) {
      u.loadJS(info.js[i], this.doc);
    }
    for (i = 0; i < numStyles; i++) {
      u.loadCSS(info.css[i], this.doc);
    }
  }

  //Toggle inactive & active ad frames during ad rotation.
  //This function is invoked on load of the ad rotation iframe
  _toggleAd(activeCID: string, inactiveCID: string) {
    try {
      let active = this._getAdDiv(activeCID),
        inactive = this._getAdDiv(inactiveCID);
      if(inactive) {
        inactive.style.display = 'block';
      }
      if(active) {
        active.style.display = 'none';
        active.innerHTML = '';
      }
    }
    catch (e) {/*continue regardless of error*/ }
  }

  /**
   * toggle ad during ad rotation
   * @param {string} adPos
   */
  toggleAD(adPos: string) {
    let adFrame = this._getActiveInactiveFrame(adPos);
    this._toggleAd(adFrame.activeContainer, adFrame.inactiveContainer);
  }

  /**
   * Populate custom var for cases where AD is load using iframeDoc.write
   * @param {window} w - window object
   * @param {window.document} d - document object
   * @param {string} id - ad position
   */
  populateCustomVar(win: window, doc: Document, id: string) {
    let info = this.adInfo[id];
    if (info === null || typeof info === 'undefined') {
      return;
    }
    //set global variables
    Object.keys(info.customVars).forEach( i => {
      win[i] = info.customVars[i];
    });
  }

  /**
   * @global
   * @typedef {Object} adPosInfo
   * @property {string} id - ad position
   * @property {string} content - ad content
   * @property {Object} [customVars] - custom variables to be populated
   * @property {boolean} adRotation - whether ad rotation is enabled for this position
   */
  /**
   * get ad info object - used for cases where ads are loaded using blankPhnx.html
   * @param {string} pos
   * @return {adPosInfo}
   */
  getAdInfo(pos: string) {
    return this.adInfo[pos];
  }

  /**
   * clear ad loading delay list
   */
  clearList() {
    this.delayList = [];
    if (this.tidDelay) {
      clearTimeout(this.tidDelay);
      this.tidDelay = 0;
    }
  }

  /**
   * @global
   * @type function
   * @callback contentCB
   * @param {string} page - pagename
   * @param {string} pos - ad position
   */
  /**
   * load ad
   * @param {string} pos
   * @param {string|contentCB} content
   * @param {Object} [cVar]
   */
  load(pos: string, content: string | (string, string) => string, cVar: Object) {
    pos = pos || '';
    content = content || '';
    if (!pos.length) {
      return false;
    }

    let as = adconfig.getPosSettings(pos);
    if (as.iframe !== undefined && !as.iframe && u.isString(content)) {
      // loading ads directly in the ad div without using iframe
      // $FlowFixMe
      return this._loadNonIframeAd(pos, content);
    }

    if (as.gscript !== undefined && as.gscript && u.isString(content)) {
      // loading global scripts/styles
      // $FlowFixMe
      return this._loadGlobalScriptCssAd(content);
    }

    let callback = (as.callback && as.callback.length) ? this.win[as.callback] : undefined;
    if (callback && callback !== undefined && u.isFunction(callback)) {
      //Trim data for callback function
      content = content.replace(/^\s+|\s+$|(\r\n)/g, '');
      let tmp2 = callback.call(this.win, adconfig.getPageName(), pos, content);
      let tmp: {bHandled: boolean, data: string} = u.extend(true, { bHandled: false, data: content }, tmp2);
      if (tmp.bHandled) {
        return true;
      }

      //data might have been modified by the callback
      content = tmp.data;
    }

    if(u.isFunction(content)) {
      // $FlowFixMe
      content = content.call(this.win, adconfig.getPageName(), pos);
    }

    if (!u.isObject(cVar)) {
      cVar = {};
    }

    var obj = {
      pos: pos,
      // $FlowFixMe
      data: content,
      customVars: cVar
    };

    //load ads
    return this._loadIframeAd(obj);
  }
}

//export singleton object as default for this module
export default new _adloader();
